import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { Pill } from '@bonniernews/dn-design-system-web/components/pill/pill.tsx'

export interface PillGroupItem {
  value: string
  text: string
  selected?: boolean
  onClick?: () => void
}

export interface PillGroupSingleSelectItem extends PillGroupItem {
  /** Required for `single-select` variant — each item links to its own page */
  href: string
}

interface PillGroupSharedProps extends SharedProps {
  size?: 'sm' | 'lg'
  label: string
}

interface PillGroupMultiSelectProps extends PillGroupSharedProps {
  variant: 'multi-select'
  items: PillGroupItem[]
}

interface PillGroupSingleSelectProps extends PillGroupSharedProps {
  variant: 'single-select'
  items: PillGroupSingleSelectItem[]
}

export type PillGroupProps = PillGroupMultiSelectProps | PillGroupSingleSelectProps

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/pill-group](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/pill-group)
 * - Storybook: [PillGroup](https://designsystem.dn.se/?path=/docs/basic-pillgroup--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/pill-group/pill-group.scss'`
 *
 * A group of multi-select pills. Two variants:
 * - `multi-select` — multiple pills can be active at once (checkbox behaviour)
 * - `single-select` — only one pill can be active at a time (radio behaviour)
 *
 * The component is stateless — selection state and `onClick` handlers are managed by the consumer.
 */
export const PillGroup = ({ items, variant, size = 'lg', label, classNames, attributes = {} }: PillGroupProps) => {
  const classes = ['ds-pill-group', classNames].filter(Boolean).join(' ')
  return (
    <div role='group' aria-label={label} className={classes} {...attributes}>
      {items.map((item) => {
        const { value, text, selected = false, onClick } = item
        const pillAttributes: Record<string, unknown> =
          variant === 'single-select' ? { role: 'radio', 'aria-checked': String(selected) } : {}

        if (variant === 'single-select') {
          const { href } = item as PillGroupSingleSelectItem
          const handleClick = onClick
            ? (e: MouseEvent) => {
                e.preventDefault()
                onClick()
              }
            : undefined

          return (
            <Pill
              key={value}
              variant='link'
              showIcon={false}
              href={href}
              text={text}
              size={size}
              selected={selected}
              attributes={{ ...pillAttributes, ...(handleClick ? { onClick: handleClick } : {}) }}
            />
          )
        }

        return (
          <Pill
            key={value}
            variant='button'
            text={text}
            size={size}
            selected={selected}
            onClick={onClick}
            attributes={pillAttributes}
          />
        )
      })}
    </div>
  )
}
